import * as React from 'react';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import {
  Building2,
  CalendarClock,
  CalendarDays,
  FileSignature,
  FileText,
  Filter,
  LayoutDashboard,
  Megaphone,
  MessageCircle,
  SlidersHorizontal,
  Landmark,
  LogOut,
  MapPin,
  Menu,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  Building,
  ShieldCheck,
  ChartColumn,
  Sparkles,
  Sun,
  Tags,
  Users,
} from 'lucide-react';
import {
  Avatar,
  AvatarFallback,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  LogoLockup,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  Toaster,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
  setTheme,
  useTheme,
} from '@aimmo/ui';
import { useAgencySession, logout } from '../lib/session.js';
import { useAbility } from '../lib/abilities.js';
import { storeCollapsed, storedCollapsed } from '../lib/sidebar.js';
import { ReferenceSearch } from '../features/reference-search.js';

export interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  exact: boolean;
}

/**
 * The app's privacy notice, on the public site. The origin is baked in at build
 * time, like the site's own canonical: production by default.
 */
const SITE_URL = (
  (import.meta.env['VITE_SITE_URL'] as string | undefined)?.trim() || 'https://www.aimmo.cloud'
).replace(/\/+$/, '');
const APP_PRIVACY_URL = `${SITE_URL}/privacy/app/`;

/** Only an admin sees the administration group (U7). */
const ADMIN_GROUP = {
  id: 'admin',
  label: 'Amministrazione',
  items: [
    { to: '/utenti', label: 'Utenti', icon: ShieldCheck, exact: false },
    { to: '/agenzia', label: 'Agenzia', icon: Building, exact: false },
  ],
} as const;

/**
 * Comunicazioni (M8). Its own group rather than an eighth entry under the work
 * items: an inbox is a place somebody sits, not a table they open, and the
 * pillars that follow — campagne, newsletter — land beside it.
 */
const COMMUNICATIONS_GROUP = {
  id: 'communications',
  label: 'Comunicazioni',
  items: [
    { to: '/comunicazioni/whatsapp', label: 'WhatsApp', icon: MessageCircle, exact: false },
    {
      to: '/comunicazioni/impostazioni',
      label: 'Impostazioni',
      icon: SlidersHorizontal,
      exact: false,
    },
  ],
} as const;

/**
 * Campagne, and the two tables it is composed out of (M9 / P2).
 *
 * Appended to Comunicazioni rather than given a group of their own — this is
 * the "campagne land beside it" the group was created for. Shown only to
 * whoever may manage a campaign: a link to a screen the API would refuse is a
 * design defect.
 */
const CAMPAIGN_ITEMS = [
  { to: '/comunicazioni/campagne', label: 'Campagne', icon: Megaphone, exact: false },
  { to: '/comunicazioni/segmenti', label: 'Segmenti', icon: Filter, exact: false },
  { to: '/comunicazioni/template', label: 'Template', icon: FileText, exact: false },
] as const;

/**
 * The data assistant (M15). Its own group for the same reason Comunicazioni is
 * one: it is a place somebody sits and asks questions, not a table they open,
 * and grouping it with Immobili and Contatti made it read as one more register.
 */
const ASSISTANT_GROUP = {
  id: 'assistant',
  label: 'Assistente',
  items: [{ to: '/assistente', label: 'AImmo', icon: Sparkles, exact: true }],
} as const;

/**
 * The spend screen, for whoever answers for it (M15.3). Its own group entry
 * rather than a second item everyone sees: what the agency is spending is
 * management information, and `read AssistantUsage` is granted to managers
 * and admins only.
 */
const ASSISTANT_USAGE_ITEM = {
  to: '/assistente/statistiche',
  label: 'Statistiche',
  icon: ChartColumn,
  exact: false,
} as const;

/**
 * Ten flat entries fit a header bar and not a sidebar. The split is by how
 * often a screen is opened: the work is on top, the tables that feed it below.
 * Administration joins as a third, role-gated group in U7.
 */
const NAV_GROUPS: readonly { id: string; label: string | null; items: readonly NavItem[] }[] = [
  {
    id: 'work',
    label: null,
    items: [
      { to: '/', label: 'Dashboard', icon: LayoutDashboard, exact: true },
      { to: '/agenda', label: 'Agenda', icon: CalendarDays, exact: false },
      { to: '/immobili', label: 'Immobili', icon: Building2, exact: false },
      { to: '/incarichi', label: 'Incarichi', icon: FileSignature, exact: false },
      { to: '/richieste', label: 'Richieste', icon: Search, exact: false },
      { to: '/contatti', label: 'Contatti', icon: Users, exact: false },
      { to: '/edifici', label: 'Edifici', icon: Landmark, exact: false },
    ],
  },
  {
    id: 'reference',
    label: 'Dati di riferimento',
    items: [
      { to: '/zone', label: 'Zone', icon: MapPin, exact: false },
      { to: '/tipologie', label: 'Tipologie', icon: Tags, exact: false },
      { to: '/tipi-appuntamento', label: 'Tipi appuntamento', icon: CalendarClock, exact: false },
    ],
  },
];

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}

/**
 * One entry of a shell's nav. Shared by the agency shell and the platform one,
 * so both mark the current page the same way — the router's own `active`
 * status, not a pathname comparison each of them wrote.
 */
export function NavLink({
  item,
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const link = (
    <Link
      to={item.to}
      activeOptions={{ exact: item.exact }}
      onClick={onNavigate}
      className={cn(
        'inline-flex h-[var(--control-height-sm)] items-center gap-2 rounded-control text-body font-medium text-ink-2 transition-colors',
        'hover:bg-surface-sunken hover:text-ink',
        'data-[status=active]:bg-accent-soft data-[status=active]:text-accent-soft-ink',
        collapsed ? 'w-[var(--control-height-sm)] justify-center px-0' : 'w-full px-2.5',
      )}
    >
      <item.icon className="size-4 shrink-0" />
      {collapsed ? <span className="sr-only">{item.label}</span> : item.label}
    </Link>
  );

  // Collapsed to an icon rail, the label has to live somewhere.
  if (!collapsed) return link;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right">{item.label}</TooltipContent>
    </Tooltip>
  );
}

function Nav({ collapsed, onNavigate }: { collapsed: boolean; onNavigate?: () => void }) {
  const ability = useAbility();
  // The inbox appears for whoever may read a conversation, which is everyone
  // who answers customers; administration stays with the admin.
  const assistantGroup = ability.can('read', 'AssistantUsage')
    ? { ...ASSISTANT_GROUP, items: [...ASSISTANT_GROUP.items, ASSISTANT_USAGE_ITEM] }
    : ASSISTANT_GROUP;
  const withAssistant = [NAV_GROUPS[0]!, assistantGroup, ...NAV_GROUPS.slice(1)];
  // Campaigns join the same group, for whoever may manage them. A manager who
  // cannot read a conversation still gets the group — the entries in it are
  // what the ability decides, not the group itself.
  const communications = ability.can('manage', 'Campaign')
    ? { ...COMMUNICATIONS_GROUP, items: [...COMMUNICATIONS_GROUP.items, ...CAMPAIGN_ITEMS] }
    : COMMUNICATIONS_GROUP;
  const withInbox =
    ability.can('read', 'Conversation') || ability.can('manage', 'Campaign')
      ? [withAssistant[0]!, withAssistant[1]!, communications, ...withAssistant.slice(2)]
      : withAssistant;
  const groups = ability.can('manage', 'User') ? [...withInbox, ADMIN_GROUP] : withInbox;

  return (
    <nav className="grid gap-4">
      {groups.map((group) => (
        <div key={group.id} className="grid gap-1">
          {group.label !== null && !collapsed && (
            <p className="px-2.5 pt-1 text-secondary font-medium tracking-wide text-muted uppercase">
              {group.label}
            </p>
          )}
          {group.items.map((item) => (
            <NavLink key={item.to} item={item} collapsed={collapsed} onNavigate={onNavigate} />
          ))}
        </div>
      ))}
    </nav>
  );
}

/**
 * Who is signed in, and the theme switch, at the foot of the nav.
 *
 * Rendered by the sidebar and by the mobile drawer alike, so there is one of
 * these rather than one per surface. Collapsed to the icon rail it is the
 * avatar alone; the name and the agency are in the menu it opens, which is
 * where they were before this moved out of the header.
 */
function AccountBlock({
  collapsed,
  contextLabel,
  userName,
  userEmail,
}: {
  collapsed: boolean;
  contextLabel: string;
  userName: string;
  userEmail: string;
}) {
  const theme = useTheme();

  const themeButton = (
    <Button
      variant="ghost"
      size="icon-sm"
      aria-label={theme === 'dark' ? 'Tema chiaro' : 'Tema scuro'}
      onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
    >
      {theme === 'dark' ? <Sun /> : <Moon />}
    </Button>
  );

  const account = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size={collapsed ? 'icon-sm' : 'sm'}
          aria-label="Account"
          className={cn(
            'min-w-0',
            collapsed ? '' : 'h-auto flex-1 justify-start gap-2 px-1.5 py-1.5',
          )}
        >
          <Avatar className="size-7 shrink-0">
            <AvatarFallback>{initials(userName)}</AvatarFallback>
          </Avatar>
          {!collapsed && (
            <span className="grid min-w-0 text-left">
              <span className="truncate text-body text-ink">{userName}</span>
              <span className="truncate text-secondary text-muted">{contextLabel}</span>
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      {/* Anchored to the side the sidebar is on, not to a header that is no
          longer there. */}
      <DropdownMenuContent side="top" align="start" className="w-60">
        <DropdownMenuLabel>{contextLabel}</DropdownMenuLabel>
        <div className="px-2 pb-1.5">
          <p className="text-body text-ink">{userName}</p>
          <p className="text-secondary text-muted">{userEmail}</p>
        </div>
        <DropdownMenuSeparator />
        {/* The notice for people who use the app: what its usage statistics
            record and what they leave out (M17, section E). It lives on the
            public site, so this is a plain link to another origin. */}
        <DropdownMenuItem asChild>
          <a href={APP_PRIVACY_URL} target="_blank" rel="noopener noreferrer">
            <ShieldCheck />
            Informativa privacy
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem tone="danger" onSelect={() => void logout()}>
          <LogOut />
          Esci
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div
      className={cn('flex items-center gap-1 border-t border-border p-2', collapsed && 'flex-col')}
    >
      {account}
      {themeButton}
    </div>
  );
}

/**
 * The shell M0 deferred (U1): a collapsible sidebar on the desktop, a drawer on
 * a phone, and the same nav rendered in both — one list of screens, not two
 * that drift apart.
 *
 * The chrome is parameterised because M12 needs a second one. A platform
 * session has no agency and no abilities, so branching inside the domain shell
 * would mean guarding every nav item against a case that must never render;
 * passing the nav in keeps one implementation and two callers.
 */
export function Shell({
  nav,
  logo,
  contextLabel,
  userName,
  userEmail,
}: {
  nav: (props: { collapsed: boolean; onNavigate?: () => void }) => React.ReactNode;
  logo: React.ReactNode;
  contextLabel: string;
  userName: string;
  userEmail: string;
}) {
  const [collapsed, setCollapsed] = React.useState(storedCollapsed);
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  // Navigating inside the drawer must close it; the route change is the signal,
  // so a link, a redirect and the back button all behave the same.
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  React.useEffect(() => setDrawerOpen(false), [pathname]);

  /**
   * Whether the matched route asked for the whole window.
   *
   * Read from the route's own `staticData` rather than matched on a pathname
   * here: a shell that knows which screens are special has to be edited every
   * time one is added, and the fact belongs to the screen. `max-w-7xl` is
   * right for a form or a table — a line of prose 1400px wide is unreadable —
   * and wrong for a chat, where the transcript, the sidebar and the composer
   * all want the room.
   */
  const fullBleed = useRouterState({
    select: (state) => state.matches.some((match) => match.staticData.fullBleed === true),
  });

  function toggleCollapsed(): void {
    setCollapsed((previous) => {
      storeCollapsed(!previous);
      return !previous;
    });
  }

  return (
    <TooltipProvider>
      <div className="min-h-dvh bg-canvas">
        <aside
          data-collapsed={collapsed}
          className={cn(
            'fixed inset-y-0 left-0 z-40 hidden flex-col border-r border-border bg-surface md:flex',
            'transition-[width] ease-standard duration-[var(--duration-enter)]',
            collapsed ? 'w-16' : 'w-60',
          )}
        >
          <div
            className={cn(
              'flex h-14 items-center border-b border-border',
              collapsed ? 'justify-center px-2' : 'justify-between px-4',
            )}
          >
            {!collapsed && logo}
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={collapsed ? 'Espandi il menù' : 'Comprimi il menù'}
              aria-expanded={!collapsed}
              onClick={toggleCollapsed}
            >
              {collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto p-2">{nav({ collapsed })}</div>

          <AccountBlock
            collapsed={collapsed}
            contextLabel={contextLabel}
            userName={userName}
            userEmail={userEmail}
          />
        </aside>

        <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
          <SheetContent side="left" className="w-72 p-0">
            <SheetTitle className="border-b border-border px-4 py-4">
              <LogoLockup />
            </SheetTitle>
            <SheetDescription className="sr-only">Navigazione principale</SheetDescription>
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="flex-1 overflow-y-auto p-2">
                {nav({ collapsed: false, onNavigate: () => setDrawerOpen(false) })}
              </div>
              <AccountBlock
                collapsed={false}
                contextLabel={contextLabel}
                userName={userName}
                userEmail={userEmail}
              />
            </div>
          </SheetContent>
        </Sheet>

        <div
          className={cn(
            // A viewport-tall flex column so a route can *opt into* filling
            // it: `main` below is the flexible child, and a page that wants
            // to own the screen — the assistant, whose composer belongs at
            // the bottom of the window rather than below a transcript —
            // takes the leftover space and scrolls inside itself.
            //
            // `min-h-dvh` alone was not enough for that: it leaves the
            // column's *height* auto, so it grows with its content and
            // `main`'s `flex-1 min-h-0` has nothing definite to shrink
            // against — the window scrolled instead of the transcript. A
            // full-bleed route gets a fixed `h-dvh` and `overflow-hidden`,
            // which makes the leftover space real and forces the scroll
            // down into whichever child asked for it. Every other route
            // keeps the old grow-with-content behaviour.
            'flex flex-col transition-[padding] ease-standard',
            fullBleed ? 'h-dvh overflow-hidden' : 'min-h-dvh',
            collapsed ? 'md:pl-16' : 'md:pl-60',
          )}
        >
          {/* Below `md` there is no sidebar — it is a drawer — so the bar
              survives there and carries only what opens it. On the desktop the
              account block moved into the sidebar foot and nothing was left to
              put here. */}
          <header className="sticky top-0 z-30 shrink-0 border-b border-border bg-surface/95 backdrop-blur md:hidden">
            <div className="flex h-14 items-center gap-2 px-4">
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Apri il menù"
                onClick={() => setDrawerOpen(true)}
              >
                <Menu />
              </Button>

              {logo}
            </div>
          </header>

          <main
            className={cn(
              'mx-auto flex min-h-0 w-full flex-1 flex-col px-4',
              fullBleed ? 'py-4' : 'max-w-7xl py-6',
            )}
          >
            <Outlet />
          </main>
        </div>

        <Toaster />
      </div>
    </TooltipProvider>
  );
}

/** The agency shell: the domain nav, the agency's name in the account menu. */
export function AppShell() {
  const session = useAgencySession();

  return (
    <Shell
      nav={({ collapsed, onNavigate }) => (
        <>
          <ReferenceSearch collapsed={collapsed} />
          <Nav collapsed={collapsed} onNavigate={onNavigate} />
        </>
      )}
      logo={
        <Link to="/" aria-label="AImmo, vai alla dashboard">
          <LogoLockup />
        </Link>
      }
      contextLabel={session.agency.name}
      userName={session.user.name}
      userEmail={session.user.email}
    />
  );
}
